import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { GlobalContext } from 'context/userContext';
import { DrawerHeader } from '@components/drawer-header';
import Tooltip from '@mui/material/Tooltip';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Paper,
  useTheme,
  Grid,
} from '@mui/material';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import BombIcon from '@mui/icons-material/Whatshot';

import type { ChecklistConfigDoc, ChecklistQuestion, ChecklistAnswerOption } from './types';
import { getChecklistConfig, getListChecklistConfigsVersions, saveChecklistConfig } from './api';
import { makeTempQid, normalizeAnswers, reindexAnswers } from './helpers';

/**
 * =========================================================
 * Configurações de UI
 * =========================================================
 */
const MAX_TEXT_LEN = 40;
const MAX_ANSWER_LEN = 18;
const MAX_ANSWERS_COUNT = 4;

const LEVELS: { value: ChecklistQuestion['minLevel']; label: string }[] = [
  { value: 'operator', label: 'Operador' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'maintenance', label: 'Manutenção' },
];

const TYPES: { value: ChecklistQuestion['type']; label: string }[] = [
  { value: 'single', label: 'Escolha Única' },
  { value: 'multi', label: 'Escolha Múltipla' },
  { value: 'numeric', label: 'Numérica' },
];

function ensureMinAnswers(q: ChecklistQuestion): ChecklistQuestion {
  if (q.type === 'numeric') return q;
  const current = (q.answers ?? []) as ChecklistAnswerOption[];

  // Só teremos single/multi
  if (current.length >= 2) return { ...q, answers: reindexAnswers(current) };

  const next = [...current];
  while (next.length < 2) next.push({ value: '', label: '' });
  return { ...q, answers: reindexAnswers(next) };
}

export function ChecklistConfigPage() {
  const { operation } = useContext(GlobalContext);
  const theme = useTheme();

  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [configs, setConfigs] = useState<{ id: string; name: string; version: number }[]>([]);
  const [selectedName, setSelectedName] = useState<string>('');

  // editor
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null);
  const [questions, setQuestions] = useState<ChecklistQuestion[]>([]);

  const bottomRef = useRef<HTMLDivElement | null>(null);

  const dbReady = !!operation;

  const helperText = useMemo(() => {
    if (!loadedVersion) return 'Novo checklist começará na versão v1';
    return `Versão carregada: v${loadedVersion} (ao salvar, cria v${loadedVersion + 1})`;
  }, [loadedVersion]);

  const refreshList = useCallback(async () => {
    if (!operation) return;
    setLoadingList(true);
    try {
      const items = await getListChecklistConfigsVersions(operation);
      setConfigs(items.map((i) => ({ id: i.id, name: i.name, version: i.version })));
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar os checklists');
    } finally {
      setLoadingList(false);
    }
  }, [operation]);

  useEffect(() => {
    setError(null);
    setSuccessMsg(null);
    if (!operation) return;
    refreshList();
  }, [operation, refreshList]);

  const applyDocToEditor = useCallback((doc: ChecklistConfigDoc) => {
    setSelectedName(doc.name);
    setName((doc.name ?? '').slice(0, MAX_TEXT_LEN));
    setNotes((doc.notes ?? '').slice(0, MAX_TEXT_LEN));
    setLoadedVersion(doc.version);
    setQuestions((doc.questions ?? []).map((q) => ensureMinAnswers({ ...q })));
  }, []);

  const loadByName = useCallback(
    async (n: string) => {
      if (!operation || !n) return;
      setError(null);
      setSuccessMsg(null);
      try {
        const doc = await getChecklistConfig(operation, n);
        applyDocToEditor(doc);
      } catch (e: any) {
        setError(e?.message ?? 'Falha ao carregar o checklist');
      }
    },
    [operation, applyDocToEditor]
  );

  // helper centralizado para atualizar UMA questão
  const updateQ = useCallback((qid: string | undefined, fn: (q: ChecklistQuestion) => ChecklistQuestion) => {
    if (!qid) return;
    setQuestions((prev) => prev.map((q) => (q.qid === qid ? fn(q) : q)));
  }, []);

  function newChecklist() {
    setSelectedName('');
    setName('');
    setNotes('');
    setLoadedVersion(null);

    const q: ChecklistQuestion = {
      qid: makeTempQid(),
      text: '',
      isBomb: false,
      minLevel: 'operator',
      type: 'single',
      answers: [{ value: '', label: '' }, { value: '', label: '' }],
    };

    setQuestions([ensureMinAnswers(q)]);
  }

  function addQuestion(scrollToEnd: boolean) {
    const q: ChecklistQuestion = {
      qid: makeTempQid(),
      text: '',
      isBomb: false,
      minLevel: 'operator',
      type: 'single',
      answers: [{ value: '', label: '' }, { value: '', label: '' }],
    };

    setQuestions((prev) => [...prev, ensureMinAnswers(q)]);

    if (scrollToEnd) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 0);
    }
  }

  function removeQuestion(qid?: string) {
    if (!qid) return;
    setQuestions((prev) => prev.filter((q) => q.qid !== qid));
  }

  // answers helpers (value automático)
  function addAnswer(qid?: string) {
    updateQ(qid, (q) => {
      const current = q.answers ?? [];
      if (current.length >= MAX_ANSWERS_COUNT) return q;
      return ensureMinAnswers({ ...q, answers: reindexAnswers([...current, { value: '', label: '' }]) });
    });
  }

  function updateAnswerLabel(qid: string | undefined, idx: number, label: string) {
    const safe = (label ?? '').slice(0, MAX_ANSWER_LEN);
    updateQ(qid, (q) => {
      const answers = q.answers ?? [];
      const next = answers.map((a, i) => (i === idx ? { ...a, label: safe } : a));
      return ensureMinAnswers({ ...q, answers: reindexAnswers(next) });
    });
  }

  function removeAnswer(qid: string | undefined, idx: number) {
    updateQ(qid, (q) => {
      const answers = (q.answers ?? []).filter((_, i) => i !== idx);
      return ensureMinAnswers({ ...q, answers: reindexAnswers(answers) });
    });
  }

  function validateBeforeSave(): string | null {
    if (!operation) return 'Selecione uma operação primeiro';
    if (!name.trim()) return 'O nome do checklist é obrigatório';

    const qs = questions
      .map((q) => ({ ...q, text: (q.text ?? '').trim() }))
      .filter((q) => q.text.length > 0);

    if (qs.length === 0) return 'Adicione pelo menos uma pergunta';

    for (const q of qs) {
      if (q.type !== 'numeric' && normalizeAnswers(q.type, q.answers).length < 2) {
        return `A pergunta "${q.text}" precisa ter pelo menos 2 opções`;
      }
    }
    return null;
  }

  async function onSave() {
    setError(null);
    setSuccessMsg(null);
    const validation = validateBeforeSave();
    if (validation) {
      setError(validation);
      return;
    }
    if (!operation) return;

    setSaving(true);
    try {
      const qs = questions
        .map((q) => {
          const text = (q.text ?? '').trim().slice(0, MAX_TEXT_LEN);
          if (!text) return null;

          const type = q.type ?? 'single';
          const answers = normalizeAnswers(type, q.answers).map((a) => ({
            value: (a.value ?? '').slice(0, MAX_TEXT_LEN),
            label: (a.label ?? '').slice(0, MAX_TEXT_LEN),
          }));

          return {
            qid: q.qid,
            text,
            isBomb: !!q.isBomb,
            minLevel: q.minLevel ?? 'operator',
            type,
            answers,
            atualizaHorimetro: !!q.atualizaHorimetro,
          } as ChecklistQuestion;
        })
        .filter(Boolean) as ChecklistQuestion[];

      const res = await saveChecklistConfig(operation, {
        name: name.trim().slice(0, MAX_TEXT_LEN),
        notes: notes.trim().slice(0, MAX_TEXT_LEN),
        questions: qs,
      });

      if (!res.ok) {
        setError(res.error ?? 'Falha ao salvar');
        return;
      }

      await refreshList();
      await loadByName(res.name);
      setSuccessMsg(`Checklist salvo com sucesso!`);
    } catch (e: any) {
      const backendError = e?.response?.data?.error || e?.response?.data?.message;
      setError(backendError ?? e?.message ?? 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  }

  async function loadByNameAndVersion(n: string, version: number) {
    if (!operation || !n) return;
    setError(null);
    setSuccessMsg(null);
    try {
      const doc = await getChecklistConfig(operation, n, version); // precisa suportar version no api.ts
      applyDocToEditor(doc);
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar checklist');
    }
  }

  function makeKey(name: string, version: number) {
    return `${name}@@${version}`;
  }
  function parseKey(key: string) {
    const [name, versionStr] = key.split('@@');
    return { name, version: Number(versionStr) };
  }

  return (
    <DrawerHeader title={'Configuração de Checklist'}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, md: 3 },
              bgcolor: 'background.paper',
              borderLeft: `6px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              mb: 1
            }}
          >
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                CADASTRO E CONFIGURAÇÃO
              </Typography>
              <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Criação de Formulários</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Crie e versione formulários de inspeção para as máquinas. Defina itens &quot;bomba&quot; (críticos) que exigem intervenção imediata da equipe de manutenção.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {!dbReady ? (
        <Alert severity="warning">Selecione uma operação (DB) antes de configurar checklists.</Alert>
      ) : (
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {successMsg && <Alert severity="success">{successMsg}</Alert>}

          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">

                <Box sx={{ minWidth: 260, width: { xs: '100%', sm: 360 } }}>
                  <TextField
                    select
                    fullWidth
                    label="Selecionar checklist"
                    value={selectedName}

                    onChange={(e) => {
                      const v = e.target.value as string;

                      if (v === '__NEW__') {
                        newChecklist(); // ou o bloco que limpa tudo
                        return;
                      }

                      setSelectedName(v);

                      const { name, version } = parseKey(v);
                      loadByNameAndVersion(name, version);
                    }}

                    InputProps={{
                      endAdornment: loadingList ? <CircularProgress size={16} /> : undefined,
                    }}
                  >
                    <MenuItem value="__NEW__">Novo</MenuItem>
                    {
                      configs.map((c) => (
                        <MenuItem key={c.id} value={makeKey(c.name, c.version)}>
                          {c.name} (v{c.version})
                        </MenuItem>
                      ))
                    }
                  </TextField>
                </Box>

                <Box sx={{ flex: 1 }} />

                <Button
                  variant="contained"
                  onClick={onSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                  Salvar (nova versão)
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Nome do checklist"
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, MAX_TEXT_LEN))}
                  helperText={helperText}
                  inputProps={{ maxLength: MAX_TEXT_LEN }}
                />
                <TextField
                  fullWidth
                  label="Observações (opcional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, MAX_TEXT_LEN))}
                  inputProps={{ maxLength: MAX_TEXT_LEN }}
                />
              </Stack>
            </CardContent>
          </Card>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Perguntas</Typography>
          </Stack>

          {questions.map((q, idx) => {
            const answers = q.answers ?? [];

            return (
              <Card key={q.qid ?? idx}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        #{idx + 1}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <IconButton onClick={() => removeQuestion(q.qid)} title="Remover pergunta">
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>

                    <TextField
                      fullWidth
                      label="Texto da pergunta"
                      value={q.text ?? ''}
                      onChange={(e) => updateQ(q.qid, (qq) => ({ ...qq, text: e.target.value.slice(0, MAX_TEXT_LEN) }))}
                      inputProps={{ maxLength: MAX_TEXT_LEN }}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 160 }}>
                        {q.type !== 'numeric' ? (
                          <>
                            <Checkbox
                              checked={!!q.isBomb}
                              onChange={(e) => updateQ(q.qid, (qq) => ({ ...qq, isBomb: e.target.checked }))}
                            />
                            <Typography>Item bomba</Typography>
                          </>
                        ) : (
                          <>
                            <Checkbox
                              checked={!!q.atualizaHorimetro}
                              onChange={(e) => updateQ(q.qid, (qq) => ({ ...qq, atualizaHorimetro: e.target.checked }))}
                            />
                            <Typography>Atualiza Horímetro</Typography>
                          </>
                        )}
                      </Stack>

                      <Box sx={{ minWidth: 220, width: { xs: '100%', sm: 260 } }}>
                        <TextField
                          select
                          fullWidth
                          label="Nível mínimo"
                          value={q.minLevel}
                          onChange={(e) => updateQ(q.qid, (qq) => ({ ...qq, minLevel: e.target.value as any }))}
                        >
                          {LEVELS.map((l) => (
                            <MenuItem key={l.value} value={l.value}>
                              {l.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>

                      <Box sx={{ minWidth: 220, width: { xs: '100%', sm: 260 } }}>
                        <TextField
                          select
                          fullWidth
                          label="Tipo"
                          value={q.type}
                          onChange={(e) => {
                            const nextType = e.target.value as ChecklistQuestion['type'];
                            updateQ(q.qid, (qq) => ensureMinAnswers({ ...qq, type: nextType }));
                          }}
                        >
                          {TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Stack>

                    {q.type !== 'numeric' && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Opções (Max {MAX_ANSWERS_COUNT})
                        </Typography>

                        <Stack spacing={1}>
                          {answers.map((a, aidx) => {
                            const highlightBomb = !!q.isBomb;
                            const isFirst = a.value === '1';

                            // se item bomba: 1 = verde (ok), 2..N = vermelho (falha)
                            const borderColor = highlightBomb ? (isFirst ? 'green' : 'red') : undefined;

                            const tooltipText = highlightBomb
                              ? isFirst
                                ? 'Item bomba: a Opção 1 representa a condição segura (OK).'
                                : 'Item bomba: opções 2..N representam condição crítica (falha / reprova).'
                              : '';

                            return (
                              <Stack key={`${q.qid}_${aidx}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                <Tooltip title={tooltipText} arrow disableHoverListener={!highlightBomb}>
                                  <TextField
                                    label={`Opção ${a.value}`}
                                    value={a.label ?? ''}
                                    onChange={(e) => updateAnswerLabel(q.qid, aidx, e.target.value)}
                                    sx={{
                                      flex: 1,
                                      ...(highlightBomb && {
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor,
                                            borderWidth: 2,
                                          },
                                          '&:hover fieldset': {
                                            borderColor,
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor,
                                          },
                                        },
                                      }),
                                    }}
                                    inputProps={{ maxLength: MAX_ANSWER_LEN }}
                                  />
                                </Tooltip>

                                {/* Ícone de bomba somente nas opções vermelhas (2..N) quando item bomba */}
                                {highlightBomb && !isFirst && (
                                  <Tooltip title="Opção crítica (bomba)" arrow>
                                    <BombIcon sx={{ color: 'red' }} />
                                  </Tooltip>
                                )}

                                <IconButton onClick={() => removeAnswer(q.qid, aidx)} title="Remover opção">
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Stack>
                            );
                          })}

                          <Button
                            onClick={() => addAnswer(q.qid)}
                            startIcon={<AddIcon />}
                            variant="outlined"
                            sx={{ width: 220 }}
                            disabled={answers.length >= MAX_ANSWERS_COUNT}
                          >
                            Adicionar opção
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            );
          })}

          {/* Botão no final para evitar scroll */}

          <Stack direction="row" justifyContent="right">
            <Button onClick={() => addQuestion(true)} startIcon={<AddIcon />} variant="contained">
              Adicionar pergunta
            </Button>
          </Stack>

          <div ref={bottomRef} />
        </Stack>
      )}
    </DrawerHeader>
  );
}
